/**
 * Phase 8 — collect reusable addresses from a customer's prior estimates.
 *
 * Pure function over the customer's estimate list. Returns the most-recent
 * unique addresses (deduped by canonical line1/line2/city/state/zip), mapped
 * to a UI-friendly shape. The spec caps the visible list at five with a
 * "more" affordance.
 *
 * Dedup rationale: a returning customer with multiple estimates at the same
 * address shouldn't see the address listed multiple times — that's
 * confusing and clutters the picker. We keep the most-recent estimate's
 * `estimate_id` (used as `reuse_from_estimate_id` on POST) and its label,
 * since the most-recent project tends to be what they're iterating on.
 */

import type { Address, Estimate } from "@starz/shared/api/types.gen";

export interface ReuseCandidate {
  estimate_id: string;
  address: Address;
  /** Optional human label combining room_label + room_type for the card. */
  label: string | null;
}

const MAX_CANDIDATES = 20;

export function reuseCandidates(estimates: Estimate[]): ReuseCandidate[] {
  const seenAddressKeys = new Set<string>();
  return estimates
    .filter((e): e is Estimate & { address: Address } => e.address !== null)
    .slice()
    .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
    .filter((e) => {
      const key = addressKey(e.address);
      if (seenAddressKeys.has(key)) return false;
      seenAddressKeys.add(key);
      return true;
    })
    .slice(0, MAX_CANDIDATES)
    .map((e) => ({
      estimate_id: e.estimate_id,
      address: e.address,
      label: pickLabel(e),
    }));
}

/** Canonical key for de-duplicating addresses. Trims + lowercases every
 * field so trivial casing/whitespace differences collapse onto the same
 * address. line2 normalises null and empty string to the same value. */
function addressKey(a: Address): string {
  const norm = (s: string | null | undefined): string =>
    (s ?? "").trim().toLowerCase();
  return [
    norm(a.line1),
    norm(a.line2),
    norm(a.city),
    norm(a.state),
    norm(a.zip),
  ].join("|");
}

function pickLabel(e: Estimate): string | null {
  if (e.room_label && e.room_label.trim().length > 0) return e.room_label;
  if (e.room_type) return e.room_type.replace(/_/g, " ");
  return null;
}
